import React, { useState } from "react";
import {
  useOutletContext,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Box, Typography, Grid, Stack, IconButton, Tooltip } from "@mui/material";
import {
  CheckCircle,
  MessageSquare,
  Calendar,
  User,
  ChevronLeft,
  Timer,
  Play,
  AlertTriangle,
  Sparkles,
  X,
} from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { useTheme as useCustomTheme } from "../../theme/useTheme";
import { incidentColors } from "../../theme/theme";

import { Card } from "../../components/Card";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Label } from "../../components/Label";
import { Separator } from "../../components/Separator";
import { useAuth } from "../../components/contexts/AuthContext";
import { ClientReadOnlyScope } from "../../components/ClientReadOnlyScope";
import { isClientReadOnly } from "../../utils/clientAccess";
import { getData } from "../../components/hooks/getData";
import SuccessPopup from "../../components/SuccessPopup";
import apiClient, { getTicketImageUrl } from "../../api-client";
import { TimeLogModal } from "./TimeLogModal";
import { TimeLogCard } from "../../components/TimeLogCard";
import { CustomSelector } from "../../components/CustomSelector";
import { Checkbox } from "../../components/Checkbox";
import { ChatInterface } from "./ChatInterface";
import CopilotSidebar from "../../components/Copilot";

// Utilidades locales reemplazando las que provenían de incidentsData
const canManageIncident = (user, incident) => {
  if (!user || !incident) return { canRecognize: false, canComplete: false };

  const allowedRoles = ["superadmin", "freelance_ticket"];
  if (!allowedRoles.includes(user.role)) {
    return { canRecognize: false, canComplete: false };
  }

  if (user.role === "freelance_ticket") {
    return {
      canRecognize: incident.status === "new",
      canComplete: incident.status === "open",
    };
  }

  // Solo los participantes pueden gestionar incidencias
  const isParticipant =
    incident.thread?.participants && incident.thread.participants.length > 0
      ? incident.thread.participants.some(
        (participant) => participant.id === user.id
      )
      : true; // Si no hay participantes específicos, cualquier usuario puede gestionar

  const isCreator = incident.created_by && incident.created_by.id === user.id;

  if (!isParticipant && !isCreator) {
    return { canRecognize: false, canComplete: false };
  }

  return {
    canRecognize: incident.status === "new",
    canComplete: incident.status === "open",
  };
};

const getCreatorName = (createdBy) => {
  if (!createdBy) return "Usuario";
  if (typeof createdBy === "object" && createdBy.full_name) {
    return createdBy.full_name;
  }
  return "Usuario";
};

// Ya no necesitamos transformar los datos del backend - los usamos directamente

const statusConfig = {
  new: {
    label: "Nuevo",
    color: incidentColors.status.new.className,
  },
  open: {
    label: "Abierto",
    color: incidentColors.status.open.className,
  },
  closed: {
    label: "Cerrado",
    color: incidentColors.status.closed.className,
  },
};

// Mapeo de estados de incidentes a colores del Badge
const incidentStatusColorMap = {
  new: "red",
  open: "yellow",
  closed: "green",
};

export function IncidentView() {
  const { incidentId } = useParams();
  const { user: currentUser, interceptorReady } = useAuth();
  const { setPageContext } = useOutletContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { colors } = useCustomTheme();
  const [isProcessing, setIsProcessing] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isPopupError, setIsPopupError] = useState(false);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [ticketImageUrl, setTicketImageUrl] = useState(null);

  // Estado local para el status del incidente
  const [localIncidentStatus, setLocalIncidentStatus] = useState(null);

  // Estados para gestión de tiempos
  const [timeLogModalOpen, setTimeLogModalOpen] = useState(false);
  const [isLoadingTicketDetails, setIsLoadingTicketDetails] = useState(false);

  // Estados para asignación, clasificación y urgencia (auto-guardado)
  const [isUpdatingAssignment, setIsUpdatingAssignment] = useState({
    assigned_to: false,
    category: false,
    is_urgent: false,
  });
  const [localAssignmentInfo, setLocalAssignmentInfo] = useState({
    assignedToId: null,
    assignedToName: null,
    category: null,
    isUrgent: null,
  });

  // Usar timestamp como trigger para refresh para hacer refresh cada vez que entramos en una incidencia
  const refreshTrigger = searchParams.get("t") || "default";

  // Estado para forzar refresh del incidente
  const [incidentRefreshKey, setIncidentRefreshKey] = useState(0);

  // refresh solo de mensajes cuando se haga un post con error
  const [refreshMessages, setrefreshMessages] = useState(0);

  // Obtener la incidencia desde el backend
  const { data: incident, isLoading: isLoadingIncident } = getData(
    `/communication/tickets/${incidentId}/`,
    interceptorReady,
    [refreshTrigger, incidentRefreshKey]
  );

  // Auto-marcar como abiertas las notificaciones de este ticket al cargar el detalle
  React.useEffect(() => {
    const ids = incident?.unopened_notification_ids;
    if (!ids?.length) return;
    apiClient
      .post("dashboard/notifications/mark-opened/", { ids })
      .catch(() => {});
  }, [incident?.id]);

  // Estado local para mensajes del chat
  const [localMessages, setLocalMessages] = useState([]);
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  const [copilotInitialPrompt, setCopilotInitialPrompt] = useState("");
  const [copilotInitialThreadTitle, setCopilotInitialThreadTitle] =
    useState("");
  const [copilotLaunchToken, setCopilotLaunchToken] = useState(0);

  React.useEffect(() => {
    setIsCopilotOpen(false);
    setCopilotInitialPrompt("");
    setCopilotInitialThreadTitle("");
  }, [incidentId]);

  // Obtener los mensajes del thread si existe
  const { data: threadMessages } = getData(
    incident?.thread?.id
      ? `/communication/threads/${incident.thread.id}/messages/all`
      : null,
    interceptorReady && !!incident?.thread?.id, // Solo hacer la llamada si existe y está listo
    [refreshTrigger, refreshMessages]
  );

  const isSuperAdmin = currentUser?.role === "superadmin";
  const isTicketFreelance = currentUser?.role === "freelance_ticket";
  const canManageAssignmentSection = isSuperAdmin || isTicketFreelance;

  // Obtener lista de asignables solo cuando se puede asignar desde UI (superadmin)
  const canAssignTickets = isSuperAdmin;
  const { data: superadminsResponse } = getData(
    "/users/?role=superadmin",
    interceptorReady && canAssignTickets,
    []
  );
  const { data: ticketFreelancersResponse } = getData(
    "/users/?role=freelance_ticket",
    interceptorReady && canAssignTickets,
    []
  );

  // Opciones para "Asignado a"
  const assignedToOptions = React.useMemo(() => {
    const normalizeList = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.results)) return payload.results;
      return [];
    };
    const assignableUsers = [
      ...normalizeList(superadminsResponse),
      ...normalizeList(ticketFreelancersResponse),
    ];
    return assignableUsers.map((user) => ({
      value: user.id,
      label:
        `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
        user.email ||
        `Usuario ${user.id}`,
    }));
  }, [superadminsResponse, ticketFreelancersResponse]);

  // Opciones para "Categoría"
  const categoryOptions = [
    { value: "soporte", label: "Soporte" },
    { value: "mentoria", label: "Mentoría" },
    { value: "consulta", label: "Consulta" },
    { value: "kit_digital", label: "Kit Digital" },
    { value: "comercial", label: "Comercial" },
  ];

  // Sincronizar mensajes del backend con estado local
  React.useEffect(() => {
    if (threadMessages && localMessages.length === 0) {
      // Solo inicializar si el estado local está vacío
      setLocalMessages(threadMessages);
    } else if (threadMessages && localMessages.length > 0) {
      // Si ya hay mensajes locales, fusionar con cuidado
      // Dar prioridad a mensajes del backend, pero mantener mensajes locales únicos
      const backendIds = new Set(threadMessages.map((msg) => msg.id));
      const uniqueLocalMessages = localMessages.filter(
        (msg) =>
          !backendIds.has(msg.id) && msg.id?.toString().startsWith("temp-")
      );
      setLocalMessages([...threadMessages, ...uniqueLocalMessages]);
    }
  }, [threadMessages]);

  const hasTicketImage = Boolean(incident?.ticket_image_url);
  React.useEffect(() => {
    let isActive = true;
    let objectUrl = null;

    if (!hasTicketImage) {
      setTicketImageUrl(null);
      return undefined;
    }

    getTicketImageUrl(incidentId)
      .then((url) => {
        objectUrl = url;
        if (isActive) setTicketImageUrl(url);
        else window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        if (isActive) setTicketImageUrl(null);
      });

    return () => {
      isActive = false;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [incidentId, hasTicketImage]);

  // Inicializar datos de asignación cuando el incidente carga
  React.useEffect(() => {
    if (incident) {
      setLocalAssignmentInfo({
        assignedToId: incident.assigned_to_info?.id || null,
        assignedToName: incident.assigned_to_info?.full_name || null,
        category: incident.category || null,
        isUrgent: incident.is_urgent ?? false,
      });
    }
  }, [incident]);

  // Función para actualizar mensajes localmente
  const handleMessagesUpdate = (messageData, action = "add") => {
    if (action === "delete") {
      // Eliminar mensaje del estado local
      setLocalMessages((prev) => prev.filter((msg) => msg.id !== messageData.id));
    } else if (action === "update") {
      // Actualizar mensaje existente
      setLocalMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageData.id
            ? { ...msg, content: messageData.content, updated_at: messageData.updated_at }
            : msg
        )
      );
    } else {
      // Agregar nuevo mensaje (comportamiento por defecto)
      setLocalMessages((prev) => [...prev, messageData]);
    }
  };

  // Función para refrescar mensajes cuando hay error al enviar
  const handleMessageSent = () => {
    setrefreshMessages((prev) => prev + 1);
  };

  const buildTicketConversationContext = () => {
    const orderedMessages = [...(localMessages || [])]
      .filter((message) => message?.message_type !== "event")
      .sort(
        (a, b) =>
          new Date(a?.created_at || 0).getTime() -
          new Date(b?.created_at || 0).getTime()
      );

    const lines = orderedMessages
      .map((message) => {
        const content = typeof message?.content === "string" ? message.content.trim() : "";
        if (!content) return null;

        const senderName =
          message?.sender?.full_name ||
          message?.sender?.email ||
          `Usuario ${message?.sender?.id || "desconocido"}`;

        const createdAt = message?.created_at
          ? new Date(message.created_at).toLocaleString("es-ES")
          : "Sin fecha";

        return `[${createdAt}] ${senderName}: ${content}`;
      })
      .filter(Boolean);

    return lines.join("\n");
  };

  const buildTicketCopilotPrompt = (ticketConversation) => {
    const safeTitle = incident?.title || "Sin título";
    const safeDescription = incident?.description?.trim() || "Sin descripción";

    return `
      Tu objetivo es sugerirme una respuesta para este ticket y un roadmap de solución usando la documentación de helpdesk.

      Contexto del ticket:
      - Ticket ID: ${incident?.id || incidentId}
      - Título: ${safeTitle}
      - Descripción: ${safeDescription}
      - Conversación completa del ticket:
      """${ticketConversation || "Sin mensajes en la conversación"}"""

      Devuélveme sin extenderte mucho:
      1) Respuesta sugerida al cliente, sin extenderte mucho y tono humano.
      2) Roadmap interno de resolución en pasos accionables.
      3) Información adicional que conviene pedir al cliente para cerrar el caso.
      4) Referencias breves a documentación usada (si aplica).
      `.trim();
        };

  const handleOpenCopilotForTicket = () => {
    if (currentUser?.role !== "superadmin") {
      setPopupMessage("Solo SuperAdmin puede usar Copilot en tickets.");
      setIsPopupError(true);
      setShowSuccessPopup(true);
      return;
    }

    const ticketConversation = buildTicketConversationContext();
    if (!incident?.description?.trim() && !ticketConversation) {
      setPopupMessage("No hay suficiente contexto (descripción o mensajes) para generar la sugerencia.");
      setIsPopupError(true);
      setShowSuccessPopup(true);
      return;
    }

    const prompt = buildTicketCopilotPrompt(ticketConversation);
    const threadTitle = `Ticket #${incident?.id || incidentId}: ${incident?.title || "Sin título"}`;
    setCopilotInitialPrompt(prompt);
    setCopilotInitialThreadTitle(threadTitle);
    setCopilotLaunchToken((prev) => prev + 1);
    setIsCopilotOpen(true);
  };

  React.useEffect(() => {
    if (incident) {
      const leftContent = (
        <IconButton
          onClick={() => navigate("/tickets")}
          size="small"
          sx={{
            color: "primary.main",
            padding: "4px",
            "&:hover": { backgroundColor: "action.hover" },
          }}
          disableRipple={false}
        >
          <ChevronLeft size={20} />
        </IconButton>
      );

      // Construir breadcrumbItems según el rol y los datos del ticket
      const breadcrumbItems =
        currentUser?.role === "superadmin"
          ? [
            { label: "Vista Global", onClick: () => navigate("/tickets") },
            {
              label: `Agencia: ${incident.agency_info?.name || "Sin agencia"
                }`,
            },
          ]
          : [
            { label: "Vista Global", onClick: () => navigate("/tickets") },
            {
              label: `Cliente: ${incident.client_info?.name || "Sin cliente"
                }`,
            },
          ];

      setPageContext({
        title: (
          <>
            Ticket:{"  "}
            <span
              style={{ color: theme.palette.text.secondary, marginLeft: "4px" }}
            >
              {incident.title}
            </span>
          </>
        ),
        showTitle: true,
        leftContent,
        breadcrumbItems,
      });
    }
  }, [incident, setPageContext, navigate, currentUser, theme]);

  // Mostrar skeleton mientras se carga
  if (isLoadingIncident) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(0, 3fr)" },
          gap: "24px",
          height: { xs: "auto", lg: "calc(100vh - 165px)" },
          minHeight: { xs: "calc(100vh - 200px)", lg: "auto" },
        }}
      >
        {/* Panel izquierdo - Skeleton */}
        <Card
          sx={{
            height: { xs: "auto", lg: "calc(100vh - 200px)" },
            minHeight: { xs: "400px", lg: "calc(100vh - 165px)" },
            maxHeight: { xs: "auto", lg: "calc(100vh - 200px)" },
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 3, flex: 1, overflowY: "auto" }}>
            <Stack spacing={3}>
              {/* Skeleton para descripción y badge */}
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        height: "16px",
                        width: "100px",
                        bgcolor: theme.palette.action.hover,
                        borderRadius: "4px",
                        mb: 1,
                      }}
                    />
                    <Box
                      sx={{
                        height: "60px",
                        width: "100%",
                        bgcolor: theme.palette.action.hover,
                        borderRadius: "4px",
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      height: "24px",
                      width: "80px",
                      bgcolor: theme.palette.action.hover,
                      borderRadius: "12px",
                      ml: 2,
                    }}
                  />
                </Box>
              </Box>

              {/* Skeleton para fechas */}
              <Box>
                <Box
                  sx={{
                    height: "16px",
                    width: "250px",
                    bgcolor: theme.palette.action.hover,
                    borderRadius: "4px",
                    mb: 1,
                  }}
                />
                <Box
                  sx={{
                    height: "16px",
                    width: "180px",
                    bgcolor: theme.palette.action.hover,
                    borderRadius: "4px",
                  }}
                />
              </Box>

              <Separator />

              {/* Skeleton para información adicional */}
              <Box>
                <Box
                  sx={{
                    height: "16px",
                    width: "80px",
                    bgcolor: theme.palette.action.hover,
                    borderRadius: "4px",
                    mb: 1,
                  }}
                />
                <Box
                  sx={{
                    height: "20px",
                    width: "150px",
                    bgcolor: theme.palette.action.hover,
                    borderRadius: "4px",
                  }}
                />
              </Box>
            </Stack>
          </Box>
        </Card>

        {/* Panel derecho - Skeleton del chat */}
        <Card
          sx={{
            height: { xs: "auto", lg: "calc(100vh - 165px)" },
            minHeight: { xs: "400px", lg: "calc(100vh - 165px)" },
            maxHeight: { xs: "auto", lg: "calc(100vh - 165px)" },
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Box
              sx={{
                height: "24px",
                width: "200px",
                bgcolor: theme.palette.action.hover,
                borderRadius: "4px",
                mb: 1,
              }}
            />
            <Box
              sx={{
                height: "16px",
                width: "150px",
                bgcolor: theme.palette.action.hover,
                borderRadius: "4px",
              }}
            />
          </Box>
          <Box sx={{ flex: 1, p: 3 }}>
            <Stack spacing={2}>
              {[1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      bgcolor: theme.palette.action.hover,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        height: "16px",
                        width: "120px",
                        bgcolor: theme.palette.action.hover,
                        borderRadius: "4px",
                        mb: 1,
                      }}
                    />
                    <Box
                      sx={{
                        height: "60px",
                        width: "100%",
                        bgcolor: theme.palette.action.hover,
                        borderRadius: "8px",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </Card>
      </Box>
    );
  }

  // Mostrar mensaje de error solo si terminó de cargar y no hay datos
  if (!incident) {
    return (
      <Card>
        <Box
          sx={{
            p: 6,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <MessageSquare size={64} color={theme.palette.text.secondary} />
          <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            Incidencia no encontrada
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            La incidencia que buscas no existe o no tienes permisos para verla
          </Typography>
          <Button onClick={() => navigate("/tickets")}>
            Volver a Tickets
          </Button>
        </Box>
      </Card>
    );
  }
  // Usar el estado local si existe, si no usar el del backend
  const currentStatus = localIncidentStatus || incident.status;
  const statusInfo = statusConfig[currentStatus];
  const ticketImageName = incident.ticket_image_name || "Imagen del ticket";

  // Crear un objeto incident con el status actualizado para calcular permisos
  const incidentWithCurrentStatus = {
    ...incident,
    status: currentStatus,
  };
  const currentIsUrgent =
    localAssignmentInfo.isUrgent ?? incident.is_urgent ?? false;

  const { canRecognize, canComplete } = canManageIncident(
    currentUser,
    incidentWithCurrentStatus
  );

  const handleComplete = async () => {
    try {
      setIsProcessing("complete");
      await apiClient.patch(`/communication/tickets/${incidentId}/`, {
        status: "closed",
      });

      // Actualizar el estado local del ticket
      setLocalIncidentStatus("closed");

      // Mostrar popup de éxito
      setPopupMessage("Ticket cerrado exitosamente");
      setIsPopupError(false);
      setShowSuccessPopup(true);
    } catch (error) {
      setPopupMessage(
        error.response?.data?.message || "No se pudo cerrar el ticket"
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleOpen = async () => {
    try {
      setIsProcessing("open");
      await apiClient.patch(`/communication/tickets/${incidentId}/`, {
        status: "open",
      });

      // Actualizar el estado local del ticket
      setLocalIncidentStatus("open");

      // Mostrar popup de éxito
      setPopupMessage("Ticket abierto exitosamente");
      setIsPopupError(false);
      setShowSuccessPopup(true);
    } catch (error) {
      setPopupMessage(
        error.response?.data?.message || "No se pudo abrir el ticket"
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsProcessing(null);
    }
  };

  // Función para abrir el modal de gestión de tiempos
  const handleOpenTimeLogModal = async () => {
    setIsLoadingTicketDetails(true);
    setTimeLogModalOpen(true);
    setIsLoadingTicketDetails(false);
  };

  // Función para cerrar el modal de gestión de tiempos
  const handleCloseTimeLogModal = () => {
    setTimeLogModalOpen(false);
  };

  // Función callback cuando se guarda exitosamente el tiempo
  const handleTimeLogSuccess = async () => {
    // Refrescar los datos del incidente sin recargar toda la página
    setIncidentRefreshKey((prev) => prev + 1);
    setPopupMessage("Tiempo guardado exitosamente");
    setIsPopupError(false);
    setShowSuccessPopup(true);
  };

  const handleAssignmentFieldChange = async (field, nextValue) => {
    if (!field) return;
    if (nextValue === null || typeof nextValue === "undefined") return;
    if (field === "assigned_to" && !isSuperAdmin) return;

    const currentAssignedToId =
      localAssignmentInfo.assignedToId ?? incident?.assigned_to_info?.id ?? null;
    const currentCategory =
      localAssignmentInfo.category ?? incident?.category ?? null;
    const currentIsUrgent =
      localAssignmentInfo.isUrgent ?? incident?.is_urgent ?? false;

    const currentValue =
      field === "assigned_to"
        ? currentAssignedToId
        : field === "category"
          ? currentCategory
          : currentIsUrgent;
    if (nextValue === currentValue) return;

    const previous = {
      assignedToId: localAssignmentInfo.assignedToId,
      assignedToName: localAssignmentInfo.assignedToName,
      category: localAssignmentInfo.category,
      isUrgent: localAssignmentInfo.isUrgent,
    };

    const assignedToOption =
      field === "assigned_to"
        ? assignedToOptions.find((option) => option.value === nextValue)
        : null;

    setLocalAssignmentInfo((prev) => ({
      assignedToId:
        field === "assigned_to" ? nextValue : prev.assignedToId,
      assignedToName:
        field === "assigned_to"
          ? assignedToOption?.label || prev.assignedToName
          : prev.assignedToName,
      category: field === "category" ? nextValue : prev.category,
      isUrgent: field === "is_urgent" ? nextValue : prev.isUrgent,
    }));

    try {
      setIsUpdatingAssignment((prev) => ({ ...prev, [field]: true }));
      await apiClient.patch(`/communication/tickets/${incidentId}/`, {
        [field]: nextValue,
      });

      setPopupMessage(
        field === "assigned_to"
          ? "Asignación actualizada exitosamente"
          : field === "category"
            ? "Categoría actualizada exitosamente"
            : "Urgencia actualizada exitosamente"
      );
      setIsPopupError(false);
      setShowSuccessPopup(true);
    } catch (error) {
      setLocalAssignmentInfo(previous);
      setPopupMessage(
        error.response?.data?.message ||
        (field === "assigned_to"
          ? "No se pudo actualizar la asignación"
          : field === "category"
            ? "No se pudo actualizar la categoría"
            : "No se pudo actualizar la urgencia")
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsUpdatingAssignment((prev) => ({ ...prev, [field]: false }));
    }
  };

  return (
    <ClientReadOnlyScope readOnly={isClientReadOnly(currentUser, incident?.client_info)}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(0, 3fr)" },
          gap: "24px",
          height: { xs: "auto", lg: "calc(100vh - 165px)" },
          minHeight: { xs: "calc(100vh - 200px)", lg: "auto" },
        }}
      >
        {/* Panel izquierdo - Información de la incidencia */}
        <Card
          sx={{
            height: { xs: "auto", lg: "calc(100vh - 200px)" },
            minHeight: { xs: "400px", lg: "calc(100vh - 165px)" },
            maxHeight: { xs: "auto", lg: "calc(100vh - 200px)" },
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 3, flex: 1, overflowY: "auto" }}>
            <Stack spacing={3}>
              {/* Header con badges */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  {/* Descripción */}
                  <Box>
                    <Label sx={{ fontSize: "1rem", marginBottom: "8px" }}>
                      Descripción
                    </Label>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        "& a": {
                          color: "#2563eb",
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        },
                      }}
                    >
                      {incident.description?.split(/(https?:\/\/[^\s]+)/g).map((part, index) => {
                        if (part.match(/^https?:\/\//)) {
                          return (
                            <a
                              key={index}
                              href={part}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {part}
                            </a>
                          );
                        }
                        return part;
                      })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {(currentUser?.role === "superadmin" || currentUser?.role === "freelance_ticket") && currentIsUrgent && (
                      <Tooltip title="Ticket urgente" arrow>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            color: theme.palette.error.main,
                          }}
                        >
                          <AlertTriangle size={22} />
                        </Box>
                      </Tooltip>
                    )}
                    <Badge
                      variant="outline"
                      color={incidentStatusColorMap[currentStatus]}
                    >
                      {statusInfo.label}
                    </Badge>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ flex: 1 }}>
                {ticketImageUrl && (
                  <Box sx={{ mb: 2 }}>
                    <Label sx={{ fontSize: "1rem", marginBottom: "8px", display: "block" }}>
                      Imagen adjunta
                    </Label>
                    <Box
                      component="img"
                      src={ticketImageUrl}
                      alt={ticketImageName}
                      onClick={() => setIsImagePreviewOpen(true)}
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: "10px",
                        objectFit: "cover",
                        cursor: "zoom-in",
                        border: `1px solid ${theme.palette.divider}`,
                        transition: "transform 0.2s ease",
                        "&:hover": {
                          transform: "scale(1.02)",
                        },
                      }}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    marginBottom: "8px",
                  }}
                >
                  <Calendar
                    size={16}
                    style={{ color: theme.palette.text.secondary }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Creada el{" "}
                    {new Date(incident.created_at).toLocaleDateString("es-ES", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <User
                    size={16}
                    style={{ color: theme.palette.text.secondary }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Por {getCreatorName(incident.created_by)}
                  </Typography>
                </Box>
              </Box>

              <Separator />

              {/* Asignación y Clasificación (superadmin y freelance_ticket en tickets new/open) */}
              {canManageAssignmentSection &&
                (incident.status === "new" || incident.status === "open") && (
                  <Box>
                    <Box sx={{ mb: 2.5 }}>
                      <Label htmlFor="is-urgent-ticket" sx={{ mb: 1, display: "block" }}>
                        Ticket urgente?
                      </Label>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Checkbox
                            id="is-urgent-ticket-no"
                            checked={!currentIsUrgent}
                            onCheckedChange={() =>
                              handleAssignmentFieldChange("is_urgent", false)
                            }
                            disabled={isUpdatingAssignment.is_urgent}
                          />
                          <Label htmlFor="is-urgent-ticket-no" sx={{ marginBottom: 0, fontWeight: 400 }}>
                            No
                          </Label>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Checkbox
                            id="is-urgent-ticket-yes"
                            checked={currentIsUrgent}
                            onCheckedChange={() =>
                              handleAssignmentFieldChange("is_urgent", true)
                            }
                            disabled={isUpdatingAssignment.is_urgent}
                          />
                          <Label htmlFor="is-urgent-ticket-yes" sx={{ marginBottom: 0, fontWeight: 400 }}>
                            Sí
                          </Label>
                        </Stack>
                      </Stack>
                    </Box>

                    {isSuperAdmin && currentStatus === "new" && (
                      <Box
                        sx={{
                          mb: 2.5,
                          p: 1.5,
                          border: `1px solid ${colors.warning.light}`,
                          backgroundColor: `${colors.warning.light}22`,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <AlertTriangle size={16} color={colors.warning.dark} />
                        <Typography variant="body2" sx={{ color: colors.warning.dark, fontWeight: 500 }}>
                          Antes de asignar ticket es necesario comprobar su urgencia!
                        </Typography>
                      </Box>
                    )}

                    <Grid container spacing={2}>
                      {isSuperAdmin && (
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Label sx={{ mb: 1, display: "block" }}>
                            Asignar a
                          </Label>
                          <CustomSelector
                            value={localAssignmentInfo.assignedToId ?? incident.assigned_to_info?.id ?? null}
                            onChange={(value) => handleAssignmentFieldChange("assigned_to", value)}
                            options={assignedToOptions}
                            placeholder="Selecciona un usuario"
                            searchable
                            disabled={isUpdatingAssignment.assigned_to}
                          />
                        </Grid>
                      )}

                      <Grid size={{ xs: 12, md: isSuperAdmin ? 6 : 12 }}>
                        <Label sx={{ mb: 1, display: "block" }}>Categoría</Label>
                        <CustomSelector
                          value={localAssignmentInfo.category ?? incident.category ?? null}
                          onChange={(value) => handleAssignmentFieldChange("category", value)}
                          options={categoryOptions}
                          placeholder="Selecciona una categoría"
                          disabled={isUpdatingAssignment.category}
                        />
                      </Grid>
                    </Grid>
                  </Box>
              )}

              {/* Separador solo si se muestra la sección de asignación */}
              {canManageAssignmentSection &&
                (incident.status === "new" || incident.status === "open") && (
                  <Separator />
                )}

              {/* Información adicional */}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Label sx={{ fontSize: "1rem" }}>Cliente</Label>
                  <Typography
                    sx={{ marginTop: "4px", color: theme.palette.text.secondary }}
                  >
                    {incident.client_info?.name || "Sin cliente"}
                  </Typography>
                </Grid>
              </Grid>

              {/* Información de tiempo registrado */}
              {(incident.time_log ||
                incident.agency_hour_balance ||
                currentUser?.role === "agency_admin") && (
                  <Box>
                    <Label sx={{ fontSize: "1rem" }}>Tiempo Registrado</Label>
                    <Box sx={{ mt: 1 }}>
                      <TimeLogCard
                        timeLog={incident.time_log}
                        agencyBalance={incident.agency_hour_balance}
                        agencyId={incident.agency_info?.id}
                      />
                    </Box>
                  </Box>
                )}

              {/* Información de participantes */}
              {incident.thread?.participants &&
                incident.thread.participants.filter((p) => p.full_name).length >
                0 && (
                  <Box>
                    <Label
                      sx={{
                        fontSize: "1rem",
                        marginBottom: "8px",
                        display: "block",
                      }}
                    >
                      Participantes (
                      {
                        incident.thread.participants.filter((p) => p.full_name)
                          .length
                      }
                      )
                    </Label>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {incident.thread.participants
                        .filter((p) => p.full_name)
                        .map((participant) => (
                          <Badge
                            key={participant.id}
                            variant="outline"
                            sx={{
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {participant.full_name}
                          </Badge>
                        ))}
                    </Box>
                  </Box>
                )}

              {/* Información de métricas */}
              {incident.metrics && (
                <Box>
                  <Label
                    sx={{
                      fontSize: "1rem",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Estadísticas de la conversación
                  </Label>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Mensajes: {incident.metrics.total_messages}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Comentarios: {incident.metrics.total_comments}
                    </Typography>
                    {incident.metrics.last_message_at && (
                      <Typography
                        variant="body1"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Último mensaje:{" "}
                        {new Date(
                          incident.metrics.last_message_at
                        ).toLocaleDateString("es-ES")}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Fechas de actualización */}
              {incident.status !== "new" && (
                <Box>
                  <Label
                    sx={{
                      fontSize: "1rem",
                    }}
                  >
                    Última actualización de la incidencia
                  </Label>
                  <Typography
                    variant="body1"
                    sx={{ marginTop: "4px", color: theme.palette.text.secondary }}
                  >
                    {new Date(incident.updated_at).toLocaleDateString("es-ES", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Botones de acción */}
          {(canComplete ||
            canRecognize ||
            currentUser?.role === "superadmin" ||
            currentUser?.role === "freelance_ticket") && (
              <Box sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                  {(currentUser?.role === "superadmin" || currentUser?.role === "freelance_ticket") && (
                    <Button
                      variant="outline"
                      onClick={handleOpenTimeLogModal}
                      disabled={isLoadingTicketDetails}
                    >
                      {isLoadingTicketDetails ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box
                            sx={{
                              width: "16px",
                              height: "16px",
                              marginRight: "8px",
                              border: "1px solid currentColor",
                              borderTop: "1px solid transparent",
                              borderRadius: "50%",
                              animation: "spin 1s linear infinite",
                              "@keyframes spin": {
                                "0%": { transform: "rotate(0deg)" },
                                "100%": { transform: "rotate(360deg)" },
                              },
                            }}
                          />
                          Cargando...
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Timer size={16} style={{ marginRight: "8px" }} />
                          {incident.time_log ? "Editar Tiempo" : "Registrar Tiempo"}
                        </Box>
      )}
                    </Button>
                  )}
                  {canRecognize && (
                    <Button onClick={handleOpen} disabled={isProcessing === "open"}>
                      {isProcessing === "open" ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box
                            sx={{
                              width: "16px",
                              height: "16px",
                              marginRight: "8px",
                              border: "1px solid currentColor",
                              borderTop: "1px solid transparent",
                              borderRadius: "50%",
                              animation: "spin 1s linear infinite",
                              "@keyframes spin": {
                                "0%": { transform: "rotate(0deg)" },
                                "100%": { transform: "rotate(360deg)" },
                              },
                            }}
                          />
                          Abriendo...
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Play size={16} style={{ marginRight: "8px" }} />
                          Abrir Ticket
                        </Box>
                      )}
                    </Button>
                  )}
                  {canComplete && (
                    <Button
                      onClick={handleComplete}
                      disabled={isProcessing === "complete"}
                    >
                      {isProcessing === "complete" ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box
                            sx={{
                              width: "16px",
                              height: "16px",
                              marginRight: "8px",
                              border: "1px solid currentColor",
                              borderTop: "1px solid transparent",
                              borderRadius: "50%",
                              animation: "spin 1s linear infinite",
                              "@keyframes spin": {
                                "0%": { transform: "rotate(0deg)" },
                                "100%": { transform: "rotate(360deg)" },
                              },
                            }}
                          />
                          Cerrando...
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CheckCircle size={16} style={{ marginRight: "8px" }} />
                          Cerrar Ticket
                        </Box>
                      )}
                    </Button>
                  )}
                </Stack>
              </Box>
            )}
        </Card>

        {/* Panel derecho - Chat de la incidencia */}
        <Card
          sx={{
            height: { xs: "auto", lg: "calc(100vh - 165px)" },
            minHeight: { xs: "400px", lg: "calc(100vh - 165px)" },
            maxHeight: { xs: "auto", lg: "calc(100vh - 165px)" },
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <ChatInterface
            currentUser={currentUser}
            customTitle={`Conversación: ${incident.title}`}
            customSubtitle="Comunicación sobre esta incidencia"
            customMessages={localMessages || []}
            threadId={incident?.thread?.id}
            participants={incident?.thread?.participants || []}
            onMessagesUpdate={handleMessagesUpdate}
            onMessageSent={handleMessageSent}
            headerActions={
              currentUser?.role === "superadmin" ? (
                <Tooltip title="Generar sugerencia IA con descripción + conversación del ticket" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleOpenCopilotForTicket}
                      sx={{
                        width: 34,
                        height: 34,
                        border: `1px solid ${theme.palette.divider}`,
                        color: colors.primary.main,
                        backgroundColor: `${colors.primary.main}10`,
                        "&:hover": {
                          backgroundColor: `${colors.primary.main}18`,
                        },
                      }}
                      aria-label="Abrir Copilot para este ticket"
                    >
                      <Sparkles size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null
            }
          />
        </Card>

        {/* Modal de gestión de tiempos */}
        <TimeLogModal
          isOpen={timeLogModalOpen}
          onClose={handleCloseTimeLogModal}
          ticketId={incidentId}
          ticketData={incident}
          onSuccess={handleTimeLogSuccess}
        />

        {isCopilotOpen ? (
          <CopilotSidebar
            key={copilotLaunchToken}
            onClose={() => setIsCopilotOpen(false)}
            initialPrompt={copilotInitialPrompt}
            initialThreadTitle={copilotInitialThreadTitle}
            autoSendOnOpen
          />
        ) : null}

        {isImagePreviewOpen && ticketImageUrl && (
          <Box
            onClick={() => setIsImagePreviewOpen(false)}
            sx={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1500,
              cursor: "pointer",
            }}
          >
            <Box
              onClick={(event) => event.stopPropagation()}
              sx={{
                position: "relative",
                maxWidth: "90vw",
                maxHeight: "90vh",
              }}
            >
              <Box
                component="img"
                src={ticketImageUrl}
                alt={ticketImageName}
                sx={{
                  maxWidth: "90vw",
                  maxHeight: "85vh",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  borderRadius: "12px",
                  display: "block",
                }}
              />
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  setIsImagePreviewOpen(false);
                }}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  color: theme.palette.common.white,
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
                }}
              >
                <X size={22} />
              </IconButton>
            </Box>
          </Box>
        )}

        {/* Success/Error Popup */}
        <SuccessPopup
          message={popupMessage}
          isOpen={showSuccessPopup}
          onClose={() => setShowSuccessPopup(false)}
          autoHideDuration={3000}
          error={isPopupError}
        />
      </Box>
    </ClientReadOnlyScope>
  );
}
